import { Directive, ElementRef, HostListener } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { isValidNumericExpression, parseNumericExpression } from './numeric-expression.helper';

/**
 * Directive that bridges a text <input> to a number model using the numeric
 * expression format validated by:  ^([+-]?\d+)(([\.,]\d*)?([eE][+-]?\d+)?)?$
 *
 * Usage: <input type="text" appNumericExpression [(ngModel)]="myNumber" />
 *
 * Implements ControlValueAccessor (string ↔ number conversion) and Validator
 * (emits { numericExpression: true } when the raw input does not match the pattern).
 */
@Directive({
  selector: 'input[appNumericExpression]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: NumericExpressionDirective, multi: true },
    { provide: NG_VALIDATORS, useExisting: NumericExpressionDirective, multi: true },
  ],
})
export class NumericExpressionDirective implements ControlValueAccessor, Validator {
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
  private hasInvalidInput = false;

  constructor(private el: ElementRef<HTMLInputElement>) {}

  @HostListener('input', ['$event.target.value'])
  onInput(raw: string): void {
    const wasInvalid = this.hasInvalidInput;
    const trimmed = raw.trim();

    if (!trimmed) {
      this.hasInvalidInput = false;
      this.onChange(null);
    } else if (isValidNumericExpression(raw)) {
      this.hasInvalidInput = false;
      this.onChange(parseNumericExpression(raw));
    } else {
      this.hasInvalidInput = true;
      // Invalid intermediate state: leave model at previous value
    }

    if (this.hasInvalidInput !== wasInvalid) {
      this.onValidatorChange();
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  // --- Validator ---

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.hasInvalidInput ? { numericExpression: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // --- ControlValueAccessor ---

  writeValue(value: number | null): void {
    this.el.nativeElement.value = value != null ? String(value) : '';
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }
}
